import { useConfigParams } from "@/hooks/use-config-params"
import { AuthType, TAuthType } from "@/lib/auth-type"
import { Ok } from "@pacote/result"
import useSWR from "swr"
import {
  confirmLoginServiceEndpoint,
  TConfirmLoginResponse,
} from "../post-login-confirm"
import { oAuthLoginEndpoint, TOAuthLoginResponse } from "../post-login-oauth"
import {
  oAuthLoginV2Endpoint,
  TOAuthLoginV2Response,
} from "../post-login-oauth-v2"
import { ssoLoginV1Endpoint, TSsoLoginResponse } from "../post-login-sso"
import { ssoLoginV3Endpoint, TSsoLoginV3Response } from "../post-login-sso-v3"

export function useLoginData() {
  const { authType } = useConfigParams()

  const dataEndpoint = getDataEndpoint(authType)

  const loginData = useSWR<
    | Ok<TSsoLoginResponse>
    | Ok<TSsoLoginV3Response>
    | Ok<TOAuthLoginResponse>
    | Ok<TOAuthLoginV2Response>
    | undefined
  >(dataEndpoint).data?.value

  return loginData
}

export function useLoginDataWithMfaConfirm() {
  const { authType } = useConfigParams()

  const dataEndpoint = getDataEndpoint(authType)

  const loginData = useSWR<
    Ok<TSsoLoginV3Response> | Ok<TOAuthLoginV2Response> | undefined
  >(dataEndpoint).data?.value

  const loginConfirmData = useSWR<Ok<TConfirmLoginResponse>>(
    confirmLoginServiceEndpoint,
  ).data?.value

  const userPassedMfaChallenge =
    loginData?.challengeRequired && loginConfirmData

  return getLoginData(
    authType,
    userPassedMfaChallenge ? loginConfirmData : loginData,
  )
}

function getDataEndpoint(authType: TAuthType) {
  if (window.APP_COUNTRY === "IT") {
    switch (authType) {
      case AuthType.OAuth:
        return oAuthLoginV2Endpoint
      case AuthType.Sso:
      default:
        return ssoLoginV3Endpoint
    }
  } else {
    switch (authType) {
      case AuthType.OAuth:
        return oAuthLoginEndpoint
      case AuthType.Sso:
      default:
        return ssoLoginV1Endpoint
    }
  }
}

function getLoginData(
  authType: TAuthType,
  loginData?:
    | TSsoLoginResponse
    | TSsoLoginV3Response
    | TOAuthLoginResponse
    | TOAuthLoginV2Response
    | TConfirmLoginResponse,
):
  | ((
      | TSsoLoginResponse
      | TSsoLoginV3Response
      | TOAuthLoginResponse
      | TOAuthLoginV2Response
      | TConfirmLoginResponse
    ) & {
      token?: string
    })
  | undefined {
  // for SSO and OAuth, the token is called "jwt" in the back-end, but has the same use.
  // To avoid having to discriminate everywhere, we just use the value of "jwt" as the "token"
  // if the "token" is not present.
  if (
    (authType === AuthType.Sso || authType === AuthType.OAuth) &&
    loginData &&
    (!("token" in loginData) || loginData.token == null) &&
    "jwt" in loginData &&
    typeof loginData.jwt === "string"
  ) {
    return { ...loginData, token: loginData.jwt }
  } else {
    return loginData
  }
}
